import { HttpService, createHttpService } from '@/services/HttpService';
import { Company, CompanyIndustry, CompanySize } from '@/app/domain/Company';
import { createUrlSearchParams } from '@/app/library/createUrlSearchParams';
import { getCountry } from '@/app/library/country-state-picker';
import { getValue } from '@/app/library/getValue';

export interface EditCompanyPayload extends Company {}

type ServerCompanyInformation = {
  company_name?: string;
  business_company_name?: string;
  reg_number?: string;
};

type CompanyInformation = {
  officialName?: string;
  brandName?: string;
  registrationNumber?: string;
};

type PartnerProfileSettingsValue<T> = {
  partner_profile: {
    profile: {
      company_information: T;
    }
  }
};

type SettingsValue<T> = {
  tenant_id?: string;
  domain_object_id?: string;
  data: T
};

export type SettingsValueResponse<T> = {
  items: SettingsValue<T>[];
};

type PartnerProfileSettingsResponse = SettingsValueResponse<PartnerProfileSettingsValue<ServerCompanyInformation>>;

type ServerContactInformation = {
  id: string;
  aan?: string;
  email?: string;
  phone?: string;
  country?: string;
  state?: string;
  city?: string;
  zipcode?: string;
  address1?: string;
  industry?: CompanyIndustry;
  organization_size?: CompanySize;
  website?: string;
};

type ContactInformation = {
  aan?: string;
  email?: string;
  phone?: string;
  country?: string;
  state?: string;
  city?: string;
  zipCode?: string;
  address?: string;
  industry?: CompanyIndustry;
  size?: CompanySize;
  website?: string;
};

type TenantResponse = {
  contact: ServerContactInformation;
};

type PutCompanyData = {
  data: {
    partner_profile: {
      profile: {
        company_information: ServerCompanyInformation;
      };
    };
  };
  tenant_id: string;
  domain_object_id: string;
};

type PutCompanyResponse = {};

type PutContactData = ServerContactInformation;

type PutContactResponse = {};

type ApplicationSettingsResponse = {
  own?: {
    value: boolean | number;
  },
  effective?: {
    value: boolean | number;
  }
};

type TenantApplicationsResponse = {
  items: string[];
};

export enum ApplicationType {
  platform = 'platform'
}

export type ApplicationInfo = {
  id: string;
  type: string;
  name: string;
};

export class CompanyService {
  private contactId: string = '';

  constructor(
    private tenantUuid: string,
    private httpService: HttpService
  ) {

  }

  public async getCompany(): Promise<Company> {
    try {
      const [
        partnerProfileSettingsResponse,
        tenantResponse
      ]: [
        PartnerProfileSettingsResponse,
        TenantResponse
      ] = await Promise.all([
        this.httpService.get<void, PartnerProfileSettingsResponse>(
          '/api/settings/v1/TENANT/namespaces/com.acronis.partners.company/settings/partner.profile.company/values',
          {
            params: createUrlSearchParams({
              tenant_id: this.tenantUuid,
              domain_object_id: this.tenantUuid
            })
          }
        ),
        this.httpService.get<void, TenantResponse>(`/api/2/tenants/${this.tenantUuid}`)
      ]);
      this.contactId = tenantResponse.contact.id;

      return {
        ...this.convertServerCompanyInformationToCompanyInformation(
          this.getServerCompanyInformation(partnerProfileSettingsResponse)
        ),
        ...this.convertServerContactInformationToContactInformation(
          this.getServerContactInformation(tenantResponse)
        )
      };
    } catch (error) {
      return {};
    }
  }

  public async editCompany(payload: EditCompanyPayload): Promise<void> {
    try {
      await Promise.all([
        this.httpService.put<PutCompanyData, PutCompanyResponse>(
          '/api/settings/v1/TENANT/namespaces/com.acronis.partners.company/settings/partner.profile.company/values',
          {
            data: {
              partner_profile: {
                profile: {
                  company_information: this.convertCompanyInformationToServerCompanyInformation(
                    this.getCompanyInformation(payload)
                  )
                }
              }
            },
            tenant_id: this.tenantUuid,
            domain_object_id: this.tenantUuid
          }
        ),
        await this.httpService.put<PutContactData, PutContactResponse>(
          `/api/2/contacts/${this.contactId}`,
          this.convertContactInformationToServerContactInformation(
            this.getContactInformation(payload)
          )
        )
      ]);
    } catch (error) {

    }
    return Promise.resolve();
  }

  public async editCompanyInfo(payload: EditCompanyPayload): Promise<void> {
    try {
      await this.httpService.put<PutCompanyData, PutCompanyResponse>(
        '/api/settings/v1/TENANT/namespaces/com.acronis.partners.company/settings/partner.profile.company/values',
        {
          data: {
            partner_profile: {
              profile: {
                company_information: this.convertCompanyInformationToServerCompanyInformation(
                  this.getCompanyInformation(payload)
                )
              }
            }
          },
          tenant_id: this.tenantUuid,
          domain_object_id: this.tenantUuid
        }
      );
    } catch (error) {

    }
    return Promise.resolve();
  }

  public async isDirectPartner(): Promise<boolean> {
    try {
      const platformApplicationId = await this.getPlatformApplicationId();

      const response: ApplicationSettingsResponse = await this.httpService.get<void, ApplicationSettingsResponse>(
        `/api/2/applications/${platformApplicationId}/settings/tenants/${this.tenantUuid}/direct_partner`
      );
      return !!response.effective?.value || !!response.own?.value;
    } catch (error) {
      return false;
    }
  }

  public async getPlatformApplicationId(): Promise<string> {
    try {
      const tenantApplicationIds: TenantApplicationsResponse = await this.httpService
        .get<void, TenantApplicationsResponse>(
          `/api/2/tenants/${this.tenantUuid}/applications`
        );

      const tenantApplications: ApplicationInfo[] = await Promise.all(
        tenantApplicationIds.items.map((applicationId) => this.getApplicationInfo(applicationId))
      );

      return tenantApplications.find((applicationInfo) => applicationInfo?.type === ApplicationType.platform)?.id || '';
    } catch (error) {
      return '';
    }
  }

  public async getApplicationInfo(applicationId: string): Promise<ApplicationInfo> {
    try {
      const response: ApplicationInfo = await this.httpService.get<void, ApplicationInfo>(
        `/api/2/applications/${applicationId}`
      );
      return response;
    } catch (error) {
      return <ApplicationInfo>{};
    }
  }

  private getServerCompanyInformation(response: PartnerProfileSettingsResponse): ServerCompanyInformation {
    return response.items[0].data?.partner_profile?.profile?.company_information;
  }

  private getCompanyInformation(payload: EditCompanyPayload): CompanyInformation {
    return {
      officialName: payload.officialName,
      brandName: payload.brandName,
      registrationNumber: payload.registrationNumber
    };
  }

  private convertServerCompanyInformationToCompanyInformation(
    serverCompanyInformation: ServerCompanyInformation
  ): CompanyInformation {
    return {
      officialName: serverCompanyInformation.company_name || undefined,
      brandName: serverCompanyInformation.business_company_name || undefined,
      registrationNumber: serverCompanyInformation.reg_number || undefined
    };
  }

  private convertCompanyInformationToServerCompanyInformation(
    companyInformation: CompanyInformation
  ): ServerCompanyInformation {
    return {
      company_name: companyInformation.officialName,
      business_company_name: companyInformation.brandName,
      reg_number: companyInformation.registrationNumber
    };
  }

  private getServerContactInformation(response: TenantResponse): ServerContactInformation {
    return response.contact;
  }

  private getContactInformation(payload: EditCompanyPayload): ContactInformation {
    return {
      email: payload.email,
      phone: payload.phone
        ? getValue(payload.phone, { prefix: '+' })
        : payload.phone,
      country: payload.country,
      state: payload.state,
      city: payload.city,
      zipCode: payload.zipCode,
      address: payload.address,
      industry: payload.industry,
      size: payload.size,
      website: payload.website
        ? getValue(payload.website, { prefix: 'https://' })
        : payload.website
    };
  }

  private convertServerContactInformationToContactInformation(
    serverContactInformation: ServerContactInformation
  ): ContactInformation {
    return {
      aan: serverContactInformation.aan || undefined,
      email: serverContactInformation.email || undefined,
      phone: serverContactInformation.phone || undefined,
      country: serverContactInformation.country ? getCountry(serverContactInformation.country)?.code : undefined,
      state: serverContactInformation.state || undefined,
      city: serverContactInformation.city || undefined,
      zipCode: serverContactInformation.zipcode || undefined,
      address: serverContactInformation.address1 || undefined,
      size: serverContactInformation.organization_size || undefined,
      industry: serverContactInformation.industry || undefined,
      website: serverContactInformation.website || undefined
    };
  }

  private convertContactInformationToServerContactInformation(
    contactInformation: ContactInformation
  ): ServerContactInformation {
    return {
      id: this.contactId,
      email: contactInformation.email,
      phone: contactInformation.phone,
      country: contactInformation.country?.toUpperCase(),
      state: contactInformation.state,
      city: contactInformation.city,
      zipcode: contactInformation.zipCode,
      address1: contactInformation.address,
      industry: contactInformation.industry,
      organization_size: contactInformation.size,
      website: contactInformation.website
    };
  }
}

export function createCompanyService(selfTenantUuid: string, tenantUuid: string): CompanyService {
  const httpService: HttpService = createHttpService(selfTenantUuid);

  return new CompanyService(tenantUuid, httpService);
}
